/**
 * 베팅슬립 상태 관리 컨텍스트
 * 싱글/멀티 카트를 독립적으로 관리: singleSelections / multiSelections
 * 외부 인터페이스(selections, addSelection, removeSelection, clearSelections, isSelected, selectionCount)는
 * 현재 betType에 따라 해당 카트를 참조하므로 기존 코드 호환 유지
 * 보너스 배당: betType 무관, 폴더수 기반으로 적용 (싱글에서도 1폴더 이상 보너스 적용 가능)
 * 수동/자동 모드: bonusConfig.mode === 'manual'이면 유저가 버튼을 눌러야 보너스 적용
 */
import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import sportsService, { MANUAL_ODDS_REFRESH_DELAY_MS } from '../services/sports';

const BettingSlipContext = createContext(null);

// WS selection_key(영문) → 카트 outcomeName(한글/영문) 매칭 (SportsPage와 동일)
const WS_TO_OUTCOME = {
  'Home': ['승', 'Home', '1'], 'Draw': ['무', 'Draw', 'X'], 'Away': ['패', 'Away', '2'],
  'Over': ['오버', 'Over'], 'Under': ['언더', 'Under'],
  '1': ['승', '1'], 'X': ['무', 'X'], '2': ['패', '2'],
};

const toNum = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : null; };

// WS 이벤트에서 카트 selection의 odds를 직접 갱신 (REST 재조회 없이 즉시)
const applyWsOddsToSelections = (selections, items) => {
  if (!Array.isArray(selections) || selections.length === 0) return selections;

  let changed = false;
  const next = selections.map(sel => {
    const gsId = sel.goalserveFixtureId ? parseInt(sel.goalserveFixtureId, 10) : null;
    if (!gsId) return sel;

    for (const item of items) {
      if (item.fixtureId !== gsId) continue;
      if (!Array.isArray(item.markets)) continue;

      // WS selections에서 is_main 마켓 우선, 전체에서 selection_key → odds 맵 구축
      const wsOddsMap = new Map();
      const sorted = [...item.markets].sort((a, b) => (b.is_main ? 1 : 0) - (a.is_main ? 1 : 0));
      for (const wsm of sorted) {
        for (const s of (wsm.selections || [])) {
          const key = s.selection_key || s.name;
          if (key && s.odds != null && !wsOddsMap.has(key)) {
            wsOddsMap.set(key, toNum(s.odds));
          }
        }
      }

      // 카트 outcomeName과 WS selection_key 매칭
      let newOdds = null;
      for (const [wsKey, wsOdds] of wsOddsMap) {
        const candidates = WS_TO_OUTCOME[wsKey];
        if (candidates && candidates.includes(sel.outcomeName)) { newOdds = wsOdds; break; }
        if (wsKey === sel.outcomeName) { newOdds = wsOdds; break; }
      }
      if (newOdds == null || newOdds === sel.odds) return sel;

      // 1X2 home/draw/away도 갱신
      const findWsOdds = (names) => {
        for (const [k, v] of wsOddsMap) { if (names.includes(k)) return v; }
        return null;
      };

      changed = true;
      return {
        ...sel,
        odds: newOdds,
        oddsHome: findWsOdds(['Home', '1']) ?? sel.oddsHome,
        oddsDraw: findWsOdds(['Draw', 'X']) ?? sel.oddsDraw,
        oddsAway: findWsOdds(['Away', '2']) ?? sel.oddsAway,
      };
    }
    return sel;
  });

  return changed ? next : selections;
};

export function BettingSlipProvider({ children }) {
  // 싱글/멀티 카트를 독립 관리
  const [singleSelections, setSingleSelections] = useState([]);
  const [multiSelections, setMultiSelections] = useState([]);
  const [betType, setBetType] = useState('single'); // 'single' 또는 'multi'
  const [betAmount, setBetAmount] = useState(0);

  // 보너스 배당 설정 캐시 (페이지 로드 시 1회 조회)
  // { enabled: boolean, mode: 'manual' | 'auto', folders: [{ folder_count, bonus_multiplier }, ...] }
  const [bonusConfig, setBonusConfig] = useState({ enabled: false, mode: 'manual', folders: [] });

  // 수동 모드에서 유저가 보너스 적용 버튼을 눌렀는지 여부
  const [bonusApplied, setBonusApplied] = useState(false);

  // 베팅슬립 싱글/멀티 탭 사용 설정 (관리자가 제어)
  // 기본값은 둘 다 true — 설정 조회 실패 시에도 기존 유저 경험 유지
  const [slipTabsConfig, setSlipTabsConfig] = useState({ enabled_single: true, enabled_multi: true });

  // [2026-05-03] 동일경기 폴더 마켓 조합 차단 페어 (관리자 운영 룰)
  //   서버측 place-bet 가드와 동일 데이터를 클라이언트에서도 즉시 차단하기 위해
  //   페이지 로드 시 1회 캐싱. 미로드 시 클라 가드는 동작 안 하지만 서버 가드는
  //   유지되므로 안전(역회귀 방지).
  const [blockedPairs, setBlockedPairs] = useState([]);

  // 현재 betType에 해당하는 카트의 항목 (derived 값, 기존 코드 호환)
  const selections = betType === 'single' ? singleSelections : multiSelections;

  // 보너스 설정 로드 (1회)
  useEffect(() => {
    sportsService.getBonusConfig().then(result => {
      if (result?.success && result?.data) {
        setBonusConfig(result.data);
      }
    });
  }, []);

  // 베팅슬립 탭 사용 설정 로드 (1회)
  useEffect(() => {
    sportsService.getSlipTabsConfig().then(result => {
      if (result?.success && result?.data) {
        setSlipTabsConfig(result.data);
      }
    });
  }, []);

  // [2026-05-03] 차단 페어 로드 (1회) — 서버 운영 룰을 클라에서도 즉시 가드
  useEffect(() => {
    sportsService.getBlockedCombinations?.().then(result => {
      if (result?.success && Array.isArray(result.data)) {
        setBlockedPairs(result.data);
      }
    }).catch(() => { /* 실패해도 서버 가드가 최종 방어 — UI 영향 없음 */ });
  }, []);

  // ── 탭 설정 변경에 따른 카트 자동 정리 ──
  // 관리자가 싱글/멀티 탭을 OFF 로 바꾸면 해당 카트의 선택을 모두 비운다 (사용자 요구).
  useEffect(() => {
    if (!slipTabsConfig.enabled_single) {
      setSingleSelections([]);
    }
    if (!slipTabsConfig.enabled_multi) {
      setMultiSelections([]);
    }
  }, [slipTabsConfig.enabled_single, slipTabsConfig.enabled_multi]);

  // ── 한쪽 탭만 활성화된 경우 betType 자동 조정 ──
  // 현재 활성 탭이 비활성화되면 반대쪽으로 강제 전환하여 끊긴 UI 상태 방지.
  useEffect(() => {
    const { enabled_single, enabled_multi } = slipTabsConfig;
    if (enabled_single && !enabled_multi && betType === 'multi') {
      setBetType('single');
    } else if (enabled_multi && !enabled_single && betType === 'single') {
      setBetType('multi');
    }
  }, [slipTabsConfig.enabled_single, slipTabsConfig.enabled_multi, betType]);

  // 선택 변경 시 수동 모드 보너스 적용 해제
  // singleSelections / multiSelections / betType 중 하나라도 바뀌면 리셋
  useEffect(() => {
    if (bonusConfig.mode === 'manual') {
      setBonusApplied(false);
    }
  }, [singleSelections.length, multiSelections.length, betType, bonusConfig.mode]);

  // 실시간 배당 갱신 — 경기 목록(SportsPage)과 동일한 방식으로 WS에서 직접 즉시 반영
  // REST 재조회 없이 WS selection_key → outcomeName 매핑으로 odds만 갱신
  useEffect(() => {
    const handler = (event) => {
      const data = event.detail?.data;
      if (!data) return;
      const items = Array.isArray(data.items) ? data.items : [data];
      if (items.length === 0) return;

      setSingleSelections(prev => applyWsOddsToSelections(prev, items));
      setMultiSelections(prev => applyWsOddsToSelections(prev, items));
    };

    window.addEventListener('sportsOddsUpdate', handler);
    return () => window.removeEventListener('sportsOddsUpdate', handler);
  }, []);

  // [2026-10-18 W93] 손등록 경기 배당·마켓 변경 알림(sportsManualOdds) → 카트에 담긴 그 경기 선택의 배당을 다시 받아 고친다.
  //   WHY: 손등록 경기는 실시간 배당 알림이 없어, 운영자가 배당을 바꿔도 카트는 예전 배당(예: 2.00)을 보여 주고
  //   접수는 바뀐 배당(1.50)으로 됐다. 회원 서버 상세 응답이 정본 — 같은 칸(outcomeId)을 찾아 배당을 바꾸고, 칸이 없어졌거나
  //   (마켓 삭제·마감·허용 범위 밖) 경기가 잠기면 카트에서 뺀다(운영자 토글 알림 처리와 같은 방식). 받기에 실패하면 그대로 둔다.
  const manualSlipRef = useRef({ single: [], multi: [] });
  manualSlipRef.current = { single: singleSelections, multi: multiSelections };
  useEffect(() => {
    const timers = new Map();
    const handler = (event) => {
      const fid = event.detail?.data?.fixtureId;
      if (fid == null) return;
      const matchId = `fixture-manual-${fid}`;
      const { single, multi } = manualSlipRef.current;
      if (![...single, ...multi].some(s => s.matchId === matchId)) return;
      if (timers.has(matchId)) clearTimeout(timers.get(matchId));
      timers.set(matchId, setTimeout(async () => {
        timers.delete(matchId);
        const detail = await sportsService.getMatchDetail(matchId);
        if (!detail || !Array.isArray(detail.markets)) return;
        const apply = (prev) => {
          let changed = false;
          const next = [];
          for (const s of prev) {
            if (s.matchId !== matchId) { next.push(s); continue; }
            const market = detail.markets.find(m => m.marketId === s.marketId);
            const outcome = (market && market.status !== 'suspended')
              ? (market.outcomes || []).find(o => o.outcomeId === s.outcomeId) : null;
            if (!outcome) { changed = true; continue; }
            if (outcome.odds !== s.odds) {
              const oddsOf = (name) => (market.outcomes || []).find(o => o.name === name)?.odds ?? null;
              changed = true;
              next.push({ ...s, odds: outcome.odds, oddsHome: oddsOf('승') ?? s.oddsHome, oddsDraw: oddsOf('무') ?? s.oddsDraw, oddsAway: oddsOf('패') ?? s.oddsAway });
            } else {
              next.push(s);
            }
          }
          return changed ? next : prev;
        };
        setSingleSelections(apply);
        setMultiSelections(apply);
      }, MANUAL_ODDS_REFRESH_DELAY_MS));
    };
    window.addEventListener('sportsManualOdds', handler);
    return () => { timers.forEach(t => clearTimeout(t)); window.removeEventListener('sportsManualOdds', handler); };
  }, []);

  // [2026-05-03 운영자 토글 즉시 반영] sportsMarketStatus 이벤트 중 source='admin_toggle' +
  //   field=is_active && value=0 (또는 is_suspended && value=1) 인 항목은 베팅슬립의 해당 라인
  //   selection 을 즉시 제거한다. matchId='fixture-{fixtureId}', marketId='mk-{fixtureId}-
  //   {marketKey}|{line}' (라인 없으면 '|line' 생략) 규칙으로 양 카트(single/multi)에서 정확히
  //   매칭. 그렇지 않으면 사용자가 운영자가 끈 라인에 베팅 진행을 시도해 백엔드에서만 차단되어
  //   UX 가 깨진다. 기존 nmsports 발신 status 이벤트(source 미지정) 는 카트 자동 정리 대상
  //   아님 — 사용자가 의도적으로 담은 라인을 nmsports tick 으로 임의 제거하지 않기 위함.
  useEffect(() => {
    const handler = (event) => {
      const data = event.detail?.data;
      if (!data) return;
      const items = Array.isArray(data.items) ? data.items : [data];
      const removals = [];
      for (const it of items) {
        if (!it || it.source !== 'admin_toggle') continue;
        const isDeactivate = (it.field === 'is_active' && Number(it.value) === 0)
          || (it.field === 'is_suspended' && Number(it.value) === 1);
        if (!isDeactivate) continue;
        const fixtureIdStr = String(it.fixtureId);
        const marketKey = String(it.marketKey || '');
        // [2026-05-03] kind='manual' 분기 — user-api transformManualMarkets 의 식별 규칙
        //   (matchId='fixture-manual-{id}', marketId='mk-manual-{id}-{marketKey}[-{group}]')
        //   에 맞춰 베팅슬립에서 정확한 selection 을 제거한다. auto 와 prefix·구분자 모두
        //   다르므로 분기 필수. it.kind 미존재 시 자동(기존 회귀 방지) 처리.
        const isManual = it.kind === 'manual';
        const lineRaw = (it.line !== undefined && it.line !== null) ? String(it.line) : '';
        if (isManual) {
          // manual: '' 또는 '0' 은 메인 그룹 → suffix 생략. 그 외 group 은 '-{group}' 부착.
          const groupSuffix = (lineRaw !== '' && lineRaw !== '0') ? `-${lineRaw}` : '';
          removals.push({
            matchId: `fixture-manual-${fixtureIdStr}`,
            marketId: `mk-manual-${fixtureIdStr}-${marketKey}${groupSuffix}`,
          });
        } else {
          const lineToken = (lineRaw !== '') ? `|${lineRaw}` : '';
          removals.push({
            matchId: `fixture-${fixtureIdStr}`,
            marketId: `mk-${fixtureIdStr}-${marketKey}${lineToken}`,
          });
        }
      }
      if (removals.length === 0) return;
      const dropFn = (prev) => prev.filter(s => !removals.some(r =>
        s.matchId === r.matchId && s.marketId === r.marketId));
      setSingleSelections(dropFn);
      setMultiSelections(dropFn);
    };
    window.addEventListener('sportsMarketStatus', handler);
    return () => window.removeEventListener('sportsMarketStatus', handler);
  }, []);

  /**
   * 베팅 항목 추가/토글 - 현재 betType의 카트에 추가
   * 같은 아웃컴은 제거(토글), 같은 경기+마켓은 교체
   *
   * [2026-05-03] 동일경기 폴더 마켓 조합 가드:
   *   같은 matchId 안에서 다른 marketId 선택을 추가할 때, blockedPairs 룰에
   *   해당하면 alert 후 추가를 거부한다. 토글(같은 outcomeId 재선택으로 제거)이나
   *   같은 marketId 교체 케이스는 이 가드를 적용하지 않는다 — 카트 행 수가
   *   증가하지 않으므로 페어가 새로 생기지 않는다.
   */
  const addSelection = useCallback((selection) => {
    const setter = betType === 'single' ? setSingleSelections : setMultiSelections;
    setter(prev => {
      const exists = prev.find(s => s.outcomeId === selection.outcomeId);
      if (exists) {
        return prev.filter(s => s.outcomeId !== selection.outcomeId);
      }
      // 같은 경기+마켓이면 교체이므로 페어 가드 스킵
      const sameMarket = prev.some(s =>
        s.matchId === selection.matchId && s.marketId === selection.marketId
      );
      // [2026-05-04] 가드 비교 기준을 selection.marketDbId(= sports_markets.id 정수) 로 교체.
      //   기존 selection.marketId 는 'mk-{fixtureId}-{marketKey}' 합성 문자열이라
      //   parseInt 결과가 NaN — 자동 경기에서 가드가 무력화되는 버그가 있었다.
      //   marketDbId 가 없으면(서버 lookup 미로드 등 예외) 클라 가드 스킵 — 서버
      //   place-bet 가드가 fallback lookup 으로 최종 방어한다.
      if (!sameMarket && blockedPairs.length > 0) {
        const newMid = parseInt(selection.marketDbId, 10);
        if (Number.isFinite(newMid) && newMid > 0) {
          for (const s of prev) {
            if (s.matchId !== selection.matchId) continue;
            const otherMid = parseInt(s.marketDbId, 10);
            if (!Number.isFinite(otherMid) || otherMid <= 0 || otherMid === newMid) continue;
            const a = Math.min(otherMid, newMid);
            const b = Math.max(otherMid, newMid);
            const hit = blockedPairs.find(p => p.market_id_1 === a && p.market_id_2 === b);
            if (hit) {
              alert(`${hit.market1_name} + ${hit.market2_name} 조합은 베팅이 불가합니다.`);
              return prev;
            }
          }
        }
      }
      const filtered = prev.filter(s =>
        !(s.matchId === selection.matchId && s.marketId === selection.marketId)
      );
      return [...filtered, selection];
    });
  }, [betType, blockedPairs]);

  /** 특정 베팅 항목 제거 - 현재 betType의 카트에서만 제거 */
  const removeSelection = useCallback((outcomeId) => {
    const setter = betType === 'single' ? setSingleSelections : setMultiSelections;
    setter(prev => prev.filter(s => s.outcomeId !== outcomeId));
  }, [betType]);

  /** 현재 betType의 카트만 초기화 (반대 카트 영향 없음) */
  const clearSelections = useCallback(() => {
    if (betType === 'single') {
      setSingleSelections([]);
    } else {
      setMultiSelections([]);
    }
    setBetAmount(0);
  }, [betType]);

  /** 양쪽 카트(싱글·멀티) 전체 초기화 + 금액·보너스 적용 상태까지 리셋
   *  WHY: 프리매치/스페셜/라이브 탭 전환 시 이전 탭에서 담은 선택지가 다른 카테고리에서
   *       잔존하면 사용자가 의도치 않은 시장에 베팅할 위험이 있다(특히 라이브 ↔ 프리매치
   *       배당·마감시간 체계가 달라 손실 발생 가능). SportsPage.handleTabChange 에서 호출. */
  const resetAllSelections = useCallback(() => {
    setSingleSelections([]);
    setMultiSelections([]);
    setBetAmount(0);
    setBonusApplied(false);
  }, []);

  /** 특정 아웃컴이 현재 betType의 카트에 선택되었는지 확인 */
  const isSelected = useCallback((outcomeId) => {
    const currentSelections = betType === 'single' ? singleSelections : multiSelections;
    return currentSelections.some(s => s.outcomeId === outcomeId);
  }, [betType, singleSelections, multiSelections]);

  // 수동 모드에서 보너스 배당 적용/해제 토글
  const toggleBonusApply = useCallback(() => {
    if (!bonusConfig.enabled) return;
    if (bonusConfig.mode !== 'manual') return;
    // 적용 가능한 보너스가 있는지 확인
    const applicable = bonusConfig.folders.filter(f => f.folder_count <= selections.length);
    if (applicable.length === 0) {
      alert('현재 폴더 수에 해당하는 보너스 배당이 없습니다.');
      return;
    }
    setBonusApplied(prev => !prev);
  }, [bonusConfig, selections.length]);

  // 기본 배당 (보너스 적용 전)
  // 싱글: 선택이 1개면 해당 배당, 여러 개면 BettingSlip에서 개별 처리하므로 0 반환
  // 멀티: 모든 선택 배당의 곱
  const baseTotalOdds = useMemo(() => {
    if (selections.length === 0) return 0;
    if (betType === 'single') {
      return selections.length === 1 ? selections[0].odds : 0;
    }
    return selections.reduce((acc, s) => acc * s.odds, 1);
  }, [selections, betType]);

  // 보너스 배수: 멀티 전용. 싱글은 항상 1.00 (보너스 미적용)
  // 수동 모드: bonusApplied가 true일 때만 적용
  const bonusMultiplier = useMemo(() => {
    if (betType === 'single') return 1.00;
    if (!bonusConfig.enabled || selections.length < 1) return 1.00;
    // 수동 모드: 유저가 보너스 적용 버튼을 눌러야 적용
    if (bonusConfig.mode === 'manual' && !bonusApplied) return 1.00;
    // "N폴더 이상" 로직: 해당 폴더수 이하의 가장 큰 folder_count 설정 적용
    const applicable = bonusConfig.folders
      .filter(f => f.folder_count <= selections.length)
      .sort((a, b) => b.folder_count - a.folder_count);
    return applicable.length > 0 ? parseFloat(applicable[0].bonus_multiplier) || 1.00 : 1.00;
  }, [bonusConfig, selections.length, bonusApplied]);

  // 최종 배당 (기본 배당 × 보너스 배수)
  const totalOdds = useMemo(() => {
    return baseTotalOdds * bonusMultiplier;
  }, [baseTotalOdds, bonusMultiplier]);

  const expectedWinnings = useMemo(() => {
    return Math.floor(betAmount * totalOdds);
  }, [betAmount, totalOdds]);

  const value = useMemo(() => ({
    selections,
    addSelection,
    removeSelection,
    clearSelections,
    resetAllSelections,
    isSelected,
    betType,
    setBetType,
    betAmount,
    setBetAmount,
    totalOdds: Math.round(totalOdds * 100) / 100,
    baseTotalOdds: Math.round(baseTotalOdds * 100) / 100,
    bonusMultiplier,
    expectedWinnings,
    selectionCount: selections.length,
    // SportsPage 뱃지용: 각 카트의 독립적인 항목 수
    singleSelectionCount: singleSelections.length,
    multiSelectionCount: multiSelections.length,
    bonusApplied,
    toggleBonusApply,
    bonusMode: bonusConfig.mode,
    // 관리자 베팅슬립 탭 설정 — BettingSlip 컴포넌트가 탭 노출·안내 제어에 사용
    slipTabsConfig,
  }), [selections, addSelection, removeSelection, clearSelections, resetAllSelections, isSelected,
       betType, betAmount, totalOdds, baseTotalOdds, bonusMultiplier, expectedWinnings,
       singleSelections.length, multiSelections.length,
       bonusApplied, toggleBonusApply, bonusConfig.mode,
       slipTabsConfig]);

  return (
    <BettingSlipContext.Provider value={value}>
      {children}
    </BettingSlipContext.Provider>
  );
}

/** 베팅슬립 컨텍스트 훅 */
export function useBettingSlip() {
  const context = useContext(BettingSlipContext);
  if (!context) {
    throw new Error('useBettingSlip은 BettingSlipProvider 내에서 사용해야 합니다');
  }
  return context;
}

export default BettingSlipContext;
